import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import emailjs from 'emailjs-com';
import { NavComponent } from '../navigationComponents/NavComponent';
import { FiInfo } from 'react-icons/fi'; // Import the info icon
import TokenDecimalsInfo from '../infoComponents/TokenDecimalsInfo';
import NoWalletInfo from '../infoComponents/NoWalletInfo';

const StyledFormContainer = styled.div`
  margin: auto;
  padding: 20px;
  background-color: #333;
  opacity: 0.95;
  text-align: center;
  width: 70%;
  border-radius: 20px;
  outline: none;
  border: transparent;
  color: white;
  font-family: Verdana, Geneva, Tahoma, sans-serif;
  box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19);
`;

const StyledHeader = styled.h1`
  opacity: 1;
  color: #b86fec;
  text-align: center;
  font-size: 2.5rem;
  text-shadow: 4px 4px 10px rgba(0, 0, 0, 0.7);
  margin-bottom: 20px;
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 15px;
  align-items: center;
`;
const StyledButton = styled.button`
  background-color: #b86fec;
  color: white;
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: #9a5ecb;
  }
`;

const SuccessMessage = styled.p`
  color: #4caf50;
  font-size: 1.2rem;
  margin-top: 20px;
`;

const promotionOptions = [
  {
    id: 0,
    title: "Option 0 – No Promotional Support",
    description: "Token delivery only. No public listing or marketing included.",
    price: "Price: €0",
  },
  {
    id: 1,
    title: "Option 1 – Basic Listing on CryptoBartl",
    description: (
      <>
        Project listed in CryptoBartl's public showcase directory TokenFactory or NFTFactory. <br/> <br/>
        Just like ShibConis {" "}
        <a
          href="https://cryptobartl.com/TokenFactory#ShibConis"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: "#b86fec",
            textDecoration: "none",
          }}
        >
          https://cryptobartl.com/TokenFactory#ShibConis
        </a> for tokens or <br/> like the OldtimerNFTs
        {" "}
        <a
          href="https://cryptobartl.com/NFTFactory#Oldtimer"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: "#b86fec",
            textDecoration: "none",
          }}
        >
          https://cryptobartl.com/NFTFactory#Oldtimer
        </a> for NFTs. <br/>
      </>
    ),
    price: "Price: €250 + €15/month*",
    hint: "Slogan and description text must be provided for this option.",
  },
  {
    id: 2,
    title: "Option 2 – Dedicated Project Page on CryptoBartl",
    description: (
      <>
        Fully branded landing page hosted on https://cryptobartl.com with token ETH sales feature (customers can automatically purchase your token for ETH). <br/>
        <br/>Just like{" "}
        <a
          href="https://cryptobartl.com/ShibConisPage"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: "#b86fec",
            textDecoration: "none",
          }}
        >
          https://cryptobartl.com/ShibConisPage
        </a>. <br/>
      </>
    ),
    price: "Price: €850 + €15/month*",
    hint: "Slogan, description text and ETH price + seller wallet address must be provided for this option. We will contact you for details also for proposing design templates.",
  },
  {
    id: 3,
    title: "Option 3 – Standalone One-Page Presentation",
    description: 
    (
      <>
        Fully branded landing page hosted on own domain. <br/>
        Would look like{" "}
        <a
          href="https://cryptobartl.com/ShibConisPage"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: "#b86fec",
            textDecoration: "none",
          }}
        >
          https://cryptobartl.com/ShibConisPage
        </a> on your own domain, but without the selling functionality. <br/>
      </>
    ),
    price: "Price: €2500 + €35/month*",
    hint: "Slogan, description text and domain name you'd like to have must be provided for this option. We will contact you for details also for proposing some design templates and different domain names.",
  },
  {
    id: 4,
    title: "Option 4 – One-Page Presentation with Sales Functionality",
    description:
    (
      <>
        Standalone website with direct purchase integration - in ETH. Would look like{" "}
        <a
          href="https://cryptobartl.com/ShibConisPage"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: "#b86fec",
            textDecoration: "none",
          }}
        >
          https://cryptobartl.com/ShibConisPage
        </a> on your own domain. <br/>
      </>
    ),
    price: "Price: €3500 + €35/month*",
    hint: "Slogan, description text and ETH price + seller wallet address and domain name you'd like to have must be provided for this option. We will contact you for details also for proposing some design templates and different domain names.",
  },
];

function TokenRequest() {
  const [formData, setFormData] = useState({
    name: '', // Default empty
    email: '', // Default empty
    package: '', // Default empty
    customizations: '', // Default empty
    additionalInfo: '', // Default empty
    cryptoWallet: 'No Wallet', // Default to "No Wallet"
    blockchain: 'Base', // Default blockchain
    tokenname: 'MyToken', // Default token name
    symbol: 'MTK', // Default token symbol
    supply: 1000000, // Default token supply
    decimals: 18, // Default decimals
    transactionLimit: '10000', // Default transaction limit
    walletLimit: '100000', // Default wallet limit
    functions: [], // Default empty array for selected functions
    redistributionPercentage: '', // Default empty
    whitelistedHolders: '', // Default empty
    metadata: '', // Default empty
    collectionName: '', // Default empty
    collectionMetadata: '', // Default empty
    promotion: promotionOptions[0]?.title || '', // Default to the first promotion option
    taxPercentageDev: 2, // Default developer tax percentage
    cryptoAddressDev: '', // Default empty
    taxPercentageMark: 5, // Default marketing tax percentage
    cryptoAddressMark: '', // Default empty
    taxPercentageChar: 3, // Default charity tax percentage
    cryptoAddressChar: '', // Default empty
  });

  const [successMessage, setSuccessMessage] = useState('');
  const selectedPackage = localStorage.getItem('LS_TokenPackage') || 'Basic Token Package';
  const [formVisible, setFormVisible] = useState(true); // State to toggle form visibility
  const [showDecimalsInfo, setShowDecimalsInfo] = useState(false); // State to toggle the info popup
  const [showNoWalletInfo, setShowNoWalletInfo] = useState(false); // State to toggle the no wallet info popup
  
  // Set the default package only once during the initial render
  useEffect(() => {
    setFormData((prevData) => ({
      ...prevData,
      package: selectedPackage,
    }));
  }, [selectedPackage]);

  const packages = [
    {
      id: 0,
      title: 'Reward Token for your Company',
      details: 'This package allows you to create a unique token for your company, which can be used as a reward for employees or customers.',
      features: ['Your Logo as your NFT', 'Your brand name as token'],
      customization: ['Name, symbol, supply', 'Decimals (default: 18)', 'Blockchain'],
      useCase: 'Ideal for companies looking to reward employees or customers with a unique token.',
      price: '€1000-€3000*',
    },
    {
      id: 1,
      title: 'Basic Token Package',
      details: 'A simple ERC-20 token with standard functionality.',
      features: ['ERC-20 Standard Token creation', 'Fixed supply (minted on deploy)', 'Basic branding (name, symbol, decimals)'],
      customization: ['Name, symbol, supply', 'Decimals (default: 18)'],
      useCase: 'Ideal for beginners, hobbyists or small projects.',
      price: '€75-€155*',
    },
    {
      id: 2,
      title: 'Advanced Token Package',
      details: 'A feature-rich token with economics and security features.',
      features: [
        'All Basic Package features',
        'Mint function',
        'Burn function',
        'Transfer tax (e.g. for dev/marketing)',
        'Anti-whale (max tx / wallet limit)',
        'Pause/unpause trading',
        'Owner-only controls'
        ],
      customization: ['Tax %, wallet limits','Burn option on/off','Ownership controls (pause/trading lock)'],
      useCase: 'Suitable for startups or businesses looking for more control, Startups, meme/token launches.',
      price: '€250-€350*',
    },
    {
      id: 3,
      title: 'Custom Token Package',
      details: 'Fully customizable token tailored to your needs.',
      features: ['All Advanced Package features', 'different Tokenomics / Transfer Mechanics', 'Governance / Control', 'Utility & Marketing', 'Dev/Launch Tools' ],
      customization: ['Token information (Name, symbol, supply, decimals)', 'Limits', 'Ownership', 'Charity', 'Presale/Airdrop'],
      useCase: 'Perfect for businesses or projects with unique requirements.',
      price: '~€500–€1,500*',
    },
    {
      id: 4,
      title: 'Single NFT Drop',
      details: 'Create a single, unique NFT.',
      features: ['ERC-721/ERC-1155 token creation', 'Metadata customization (image, description, attributes)', 'Deployment and minting on a blockchain of your choice'],
      customization: ['Image', 'Metadata'],
      useCase: 'Ideal for artists or individuals looking to tokenize a single asset.',
      price: '€25-€125',
    },
    {
      id: 5,
      title: 'Collection NFT Drop (Up to 50 NFTs)',
      details: 'Create a collection of NFTs.',
      features: ['NFT Collection creation', 'ERC-721/ERC-1155 token creation', 'Metadata customization (image, description, attributes)', 'Deployment and minting on a blockchain of your choice'],
      customization: ['Image x NFTs', 'Metadata x NFTs', 'Collection Name'],
      useCase: 'Perfect for artists, brands, or businesses launching a large-scale NFT project.',
      price: '€50 + €15-€130 per NFT*',
    },
  ];

  

  {/* Tax Percentage and Crypto Address Fields */}
const taxFields = [
  {
    id: 'taxPercentageDev',
    name: 'taxPercentageDev',
    label: 'Tax %:',
    placeholder: 'Tax Percentage',
    defaultValue: 2,
    cryptoLabel: 'Founder Crypto Address:',
    cryptoId: 'cryptoAddressDev',
    cryptoName: 'cryptoAddressDev',
    cryptoPlaceholder: 'Founder Crypto Address',
  },
  {
    id: 'taxPercentageMark',
    name: 'taxPercentageMark',
    label: 'Tax %:',
    placeholder: 'Tax Percentage',
    defaultValue: 5,
    cryptoLabel: 'Marketing Crypto Address:',
    cryptoId: 'cryptoAddressMark',
    cryptoName: 'cryptoAddressMark',
    cryptoPlaceholder: 'Marketing Crypto Address',
  },
  {
    id: 'taxPercentageChar',
    name: 'taxPercentageChar',
    label: 'Tax %:',
    placeholder: 'Tax Percentage',
    defaultValue: 3,
    cryptoLabel: 'Charity Crypto Address:',
    cryptoId: 'cryptoAddressChar',
    cryptoName: 'cryptoAddressChar',
    cryptoPlaceholder: 'Charity Crypto Address',
  },
];


  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (name === 'package') {
      console.log('Selected Package:', value); // Log the selected package name
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Simulate sending an email (replace this with your email API)
    console.log('Form Data:', formData);

    
    emailjs
    .send(
      'service_nsv4din', // EmailJS service ID
      'template_lpdoeug', // EmailJS template ID
      formData, // Pass the formData object
      'T7RpkGJri8FRA6-PP' // Replace with your EmailJS user ID
    )
    .then((response) => {
      console.log('SUCCESS!', response.status, response.text);
    })
    .catch((error) => {
      console.error('FAILED...', error);
    });

    // Reset form and show success message
    setFormData({
      name: '',
      email: '',
      package: '',
      customizations: '',
      additionalInfo: '',
      cryptoWallet: '',

      decimals: 18, // Default decimals
      blockchain: 'Base', // Default blockchain
      transactionLimit: '',
      walletLimit: '',
      functions: [], // Array for selected functions
      redistributionPercentage: '',
      whitelistedHolders: '',
      metadata: '',
      collectionName: '',
      collectionMetadata: '',
      promotion: '', // Selected promotion option
      taxPercentageDev: '',
      cryptoAddressDev: '',
      taxPercentageMark: '',
      cryptoAddressMark: '',
      taxPercentageChar: '',
      cryptoAddressChar: '',
    });

    console.log(selectedPackage)

    setSuccessMessage('Your request has been sent successfully!');

    setTimeout(() => {
      setFormVisible(false); // Hide the form
    }, 3000); // 3 seconds
  };

  return (
    <>
      <NavComponent />
      <br />
      <br />
      {formVisible ? (
      <StyledFormContainer>
        <StyledHeader>Request Your Token / NFT</StyledHeader>
        <br />
        <br />
        <StyledForm onSubmit={handleSubmit}>
          {/* Name Input */}
          <div style={{ textAlign: 'left', width: '80%' }}>
            <label htmlFor="name" style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>
              Your Name:
            </label>
            <input
              id="name"
              type="text"
              name="name"
              placeholder="Your Name"
              value={formData.name}
              onChange={handleChange}
              required
              style={{
                width: '100%',
                padding: '10px',
                borderRadius: '8px',
                border: '1px solid #b86fec',
                fontSize: '1rem',
              }}
            />
          </div>

          {/* Email Input */}
          <div style={{ textAlign: 'left', width: '80%' }}>
            <label htmlFor="email" style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>
              Your Email:
            </label>
            <input
              id="email"
              type="email"
              name="email"
              placeholder="Your Email"
              value={formData.email}
              onChange={handleChange}
              required
              style={{
                width: '100%',
                padding: '10px',
                borderRadius: '8px',
                border: '1px solid #b86fec',
                fontSize: '1rem',
              }}
            />
          </div>
          
          {/* Crypto Wallet Address Input */}
          <div style={{ textAlign: 'left', width: '80%' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
            <label htmlFor="cryptoWallet" style={{ fontWeight: 'bold', marginBottom: '0', display: 'block' }}>
              Your Crypto Wallet Address (or 'no wallet' + €150):
            </label>
            {/* Button to show info */}
            <button
              onClick={() => setShowNoWalletInfo(true)} // Show the info popup
              style={{
                backgroundColor: '#b86fec',
                color: '#fff',
                border: 'none',
                borderRadius: '50%', // Makes the button circular
                width: '40px', // Sets width for the circular button
                height: '40px', // Sets height for the circular button
                fontSize: '1.2rem', // Slightly larger font for the "i"
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <FiInfo /> {/* Render the info icon */}
            </button>
          </div>
            <input
              id="cryptoWallet"
              type="text"
              name="cryptoWallet"
              placeholder="Your Crypto Wallet Address"
              value={formData.cryptoWallet}
              onChange={handleChange}
              required
              style={{
                width: '100%',
                padding: '10px',
                borderRadius: '8px',
                border: '1px solid #b86fec',
                fontSize: '1rem',
              }}
            />
            {/* WalletInfo Info Popup */}
            {showNoWalletInfo && <NoWalletInfo setShowNoWalletInfo={setShowNoWalletInfo} />}
          </div>

          {/* Blockchain Selection */}
          <div style={{ textAlign: 'left', width: '80%', marginTop: '20px' }}>
            <label htmlFor="blockchain" style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>
              Select Blockchain:
            </label>
            <select
              id="blockchain"
              name="blockchain"
              value={formData.blockchain || 'Base'} // Default to Ethereum
              onChange={handleChange}
              required
              style={{
                width: '100%',
                padding: '10px',
                borderRadius: '8px',
                border: '1px solid #b86fec',
                fontSize: '1rem',
                backgroundColor: '#fff',
                color: '#333',
              }}
            >
              <option value="Ethereum">Ethereum</option>
              <option value="Arbitrum">Arbitrum</option>
              <option value="Base">Base</option>
              <option value="Others">Others (write it in the additional information section)</option>
            </select>
          </div>

          

          {/* Package Selection */}
          <div style={{ textAlign: 'left', width: '80%' }}>
            <label htmlFor="package" style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>
              Select a Package:
            </label>
            <select
              id="package"
              name="package"
              value={formData.package} // Use the package from formData
              onChange={handleChange}
              required
              style={{
                width: '100%',
                padding: '10px',
                borderRadius: '8px',
                border: '1px solid #b86fec',
                fontSize: '1rem',
                backgroundColor: '#fff',
                color: '#333',
              }}
            >
              <option value="" disabled>
                -- Select a Package --
              </option>
              {packages.map((pkg, index) => (
                <option key={index} value={pkg.title}>
                  {pkg.title} {/* Display the title of each package */}
                </option>
              ))}
            </select>
          </div>

          <div>
            <div>
            {formData.package === 'Reward Token for your Company' && (
                <div>
                  <h3 style={{ color: '#b86fec', marginBottom: '10px', textAlign: 'center' }}>
                    <strong>A unique token for your company, which can be used as a reward for employees or customers.</strong> <br />
                    Price: €1000-€3000*
                    <p
                    style={{
                      fontSize: '0.9rem',
                      color: '#888',
                      textAlign: 'center',
                      marginTop: '5px',
                      fontStyle: 'italic',
                    }}
                  >
                    *Price depends on the chosen blockchain and customizations
                  </p>
                  </h3>
                  
                  <ul style={{ listStyleType: 'none', padding: '0', margin: '0', color: 'white' }}>
                    <li style={{ marginBottom: '10px' }}>
                      <span style={{ color: '#b86fec', fontWeight: 'bold' }}>•</span> Company's Logo as NFT
                    </li>
                    <li style={{ marginBottom: '10px' }}>
                      <span style={{ color: '#b86fec', fontWeight: 'bold' }}>•</span> Company's Name as ERC20 Token
                    </li>
                    <li style={{ marginBottom: '10px' }}>
                      <span style={{ color: '#b86fec', fontWeight: 'bold' }}>•</span> Fixed supply (minted on deploy)
                    </li>
                    <li style={{ marginBottom: '10px' }}>
                      <span style={{ color: '#b86fec', fontWeight: 'bold' }}>•</span> Basic branding (name, symbol, decimals)
                    </li>
                  </ul>
                </div>
              )}
              {formData.package === 'Basic Token Package' && (
                <div>
                  <h3 style={{ color: '#b86fec', marginBottom: '10px', textAlign: 'center' }}>
                    <strong>A simple ERC-20 token with standard functionality.</strong> <br />
                    Price: €380-€550*
                    <p
                    style={{
                      fontSize: '0.9rem',
                      color: '#888',
                      textAlign: 'center',
                      marginTop: '5px',
                      fontStyle: 'italic',
                    }}
                  >
                    *Price depends on the chosen blockchain and customizations
                  </p>
                  </h3>
                  
                  <ul style={{ listStyleType: 'none', padding: '0', margin: '0', color: 'white' }}>
                    <li style={{ marginBottom: '10px' }}>
                      <span style={{ color: '#b86fec', fontWeight: 'bold' }}>•</span> ERC-20 Standard Token creation
                    </li>
                    <li style={{ marginBottom: '10px' }}>
                      <span style={{ color: '#b86fec', fontWeight: 'bold' }}>•</span> Fixed supply (minted on deploy)
                    </li>
                    <li style={{ marginBottom: '10px' }}>
                      <span style={{ color: '#b86fec', fontWeight: 'bold' }}>•</span> Basic branding (name, symbol, decimals)
                    </li>
                  </ul>
                </div>
              )}
              {formData.package === 'Advanced Token Package' && (
                <div>
                  <h3 style={{ color: '#b86fec', marginBottom: '10px', textAlign: 'center' }}>
                    <strong>A feature-rich token with economics and security features.</strong> <br />
                    Price: €950-€1350*
                    <p
                    style={{
                      fontSize: '0.9rem',
                      color: '#888',
                      textAlign: 'center',
                      marginTop: '5px',
                      fontStyle: 'italic',
                    }}
                  >
                    *Price depends on the chosen blockchain and customizations
                  </p>
                  </h3>
                  <ul style={{ listStyleType: 'none', padding: '0', margin: '0', color: 'white' }}>
                    <li style={{ marginBottom: '10px' }}>
                      <span style={{ color: '#b86fec', fontWeight: 'bold' }}>•</span> All Basic Package features
                    </li>
                    <li style={{ marginBottom: '10px' }}>
                      <span style={{ color: '#b86fec', fontWeight: 'bold' }}>•</span> Mint function
                    </li>
                    <li style={{ marginBottom: '10px' }}>
                      <span style={{ color: '#b86fec', fontWeight: 'bold' }}>•</span> Burn function
                    </li>
                    <li style={{ marginBottom: '10px' }}>
                      <span style={{ color: '#b86fec', fontWeight: 'bold' }}>•</span> Transfer tax (e.g., for dev/marketing/charity)
                    </li>
                    <li style={{ marginBottom: '10px' }}>
                      <span style={{ color: '#b86fec', fontWeight: 'bold' }}>•</span> Anti-whale (max tx / wallet limit)
                    </li>
                    <li style={{ marginBottom: '10px' }}>
                      <span style={{ color: '#b86fec', fontWeight: 'bold' }}>•</span> Pause/unpause trading
                    </li>
                    <li style={{ marginBottom: '10px' }}>
                      <span style={{ color: '#b86fec', fontWeight: 'bold' }}>•</span> Owner-only controls
                    </li>
                  </ul>
                </div>
              )}
              {formData.package === 'Custom Token Package' && (
                <div>
                  <h3 style={{ color: '#b86fec', marginBottom: '10px', textAlign: 'center' }}>
                    <strong>Fully customizable token tailored to your needs.</strong>
                  </h3>
                  <ul style={{ listStyleType: 'none', padding: '0', margin: '0', color: 'white' }}>
                    <li style={{ marginBottom: '10px' }}>
                      <span style={{ color: '#b86fec', fontWeight: 'bold' }}>•</span> All Advanced Package features
                    </li>
                    <li style={{ marginBottom: '10px' }}>
                      <span style={{ color: '#b86fec', fontWeight: 'bold' }}>•</span> Different tokenomics / transfer mechanics
                    </li>
                    <li style={{ marginBottom: '10px' }}>
                      <span style={{ color: '#b86fec', fontWeight: 'bold' }}>•</span> Governance / control
                    </li>
                    <li style={{ marginBottom: '10px' }}>
                      <span style={{ color: '#b86fec', fontWeight: 'bold' }}>•</span> Utility & marketing
                    </li>
                    <li style={{ marginBottom: '10px' }}>
                      <span style={{ color: '#b86fec', fontWeight: 'bold' }}>•</span> Dev/launch tools
                    </li>
                  </ul>
                </div>
              )}
              {formData.package === 'Single NFT Drop' && (
                <div>
                  <h3 style={{ color: '#b86fec', marginBottom: '10px', textAlign: 'center' }}>
                    <strong>Create a single, unique NFT.</strong><br />
                    Price: €25-€125*
                    <p
                    style={{
                      fontSize: '0.9rem',
                      color: '#888',
                      textAlign: 'center',
                      marginTop: '5px',
                      fontStyle: 'italic',
                    }}
                  >
                    *Price depends on the chosen blockchain and customizations
                  </p>
                  </h3>
                  <ul style={{ listStyleType: 'none', padding: '0', margin: '0', color: 'white' }}>
                    <li style={{ marginBottom: '10px' }}>
                      <span style={{ color: '#b86fec', fontWeight: 'bold' }}>•</span> ERC-721/ERC-1155 token creation
                    </li>
                    <li style={{ marginBottom: '10px' }}>
                      <span style={{ color: '#b86fec', fontWeight: 'bold' }}>•</span> Metadata customization (image, description, attributes)
                    </li>
                    <li style={{ marginBottom: '10px' }}>
                      <span style={{ color: '#b86fec', fontWeight: 'bold' }}>•</span> Deployment and minting on a blockchain of your choice
                    </li>
                  </ul>
                </div>
              )}
              {formData.package === 'Collection NFT Drop (Up to 50 NFTs)' && (
                <div>
                  <h3 style={{ color: '#b86fec', marginBottom: '10px', textAlign: 'center' }}>
                    <strong>Create a collection of NFTs with up to 50 items.</strong><br />
                    Price: €50 + €15-€130 per NFT*
                    <p
                    style={{
                      fontSize: '0.9rem',
                      color: '#888',
                      textAlign: 'center',
                      marginTop: '5px',
                      fontStyle: 'italic',
                    }}
                  >
                    *Price depends on the chosen blockchain and customizations
                  </p>
                  </h3>
                  <ul style={{ listStyleType: 'none', padding: '0', margin: '0', color: 'white' }}>
                    <li style={{ marginBottom: '10px' }}>
                      <span style={{ color: '#b86fec', fontWeight: 'bold' }}>•</span> NFT Collection creation
                    </li>
                    <li style={{ marginBottom: '10px' }}>
                      <span style={{ color: '#b86fec', fontWeight: 'bold' }}>•</span> ERC-721/ERC-1155 token creation
                    </li>
                    <li style={{ marginBottom: '10px' }}>
                      <span style={{ color: '#b86fec', fontWeight: 'bold' }}>•</span> Metadata customization (image, description, attributes)
                    </li>
                    <li style={{ marginBottom: '10px' }}>
                      <span style={{ color: '#b86fec', fontWeight: 'bold' }}>•</span> Deployment and minting on a blockchain of your choice
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* Logo NFT */}
          <div>
            <p>For NFT creation, please note that the logo image and data stored among must be sent to us via email. We will contact you separately with instructions for submitting your image after receiving your request for the token creation.</p>
            <p style={{color: '#b86fec'}}>A generic NFT (same for all employees): 450€ <br/> A specific NFT for each employee: 280€ each <br/>Wallet + NFT&Token Delivery: +150€ each </p>
          </div>


          {/* Customizations */}
          <div style={{ textAlign: 'left', width: '80%' }}>
            <label htmlFor="customizations" style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>
              Customizations:
            </label>
            {formData.package === 'Reward Token for your Company' && (
              <div>
                <label htmlFor="rewardTokenSubPackage" style={{ color: '#b86fec', fontWeight: 'bold', marginBottom: '10px', display: 'block' }}>
                  Choose your Reward Token Package:
                </label>
                <select
                  id="rewardTokenSubPackage"
                  name="rewardTokenSubPackage"
                  value={formData.rewardTokenSubPackage || ''}
                  onChange={handleChange}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    border: '1px solid #b86fec',
                    fontSize: '1rem',
                    marginBottom: '10px',
                  }}
                >
                  <option value="" disabled>-- Select a Sub-Package --</option>
                  <option value="Basic Token Package">Basic Token Package</option>
                  <option value="Advanced Token Package">Advanced Token Package</option>
                </select>

                {/* Show the customizations for the selected sub-package */}
                {formData.rewardTokenSubPackage === 'Basic Token Package' && (
                  <div style={{ marginTop: '10px', border: '1px solid #b86fec', borderRadius: '8px', padding: '10px' }}>
                    <strong style={{ color: '#b86fec' }}>Basic Token Package Features:</strong>
                    <ul style={{ color: 'white', marginTop: '8px' }}>
                      <li>ERC-20 Standard Token creation</li>
                      <li>Fixed supply (minted on deploy)</li>
                      <li>Basic branding (name, symbol, decimals)</li>
                    </ul>
                    {/* Basic Token Customization Fields */}
                    <label htmlFor="tokenname" style={{ color: '#b86fec', marginBottom: '5px', display: 'block' }}>Token Name:</label>
                    <input type="text" id="tokenname" name="tokenname" placeholder="Token Name" value={formData.tokenname || 'MyToken'} onChange={handleChange} required style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #b86fec', fontSize: '1rem', marginBottom: '10px' }} />
                    <label htmlFor="symbol" style={{ color: '#b86fec', marginBottom: '5px', display: 'block' }}>Token Symbol:</label>
                    <input type="text" id="symbol" name="symbol" placeholder="Token Symbol" value={formData.symbol || 'MTK'} onChange={handleChange} required style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #b86fec', fontSize: '1rem', marginBottom: '10px' }} />
                    <label htmlFor="supply" style={{ color: '#b86fec', marginBottom: '5px', display: 'block' }}>Token Supply:</label>
                    <input type="number" id="supply" name="supply" placeholder="Token Supply" value={formData.supply || 1000000} onChange={handleChange} required style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #b86fec', fontSize: '1rem', marginBottom: '10px' }} />
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                      <label htmlFor="decimals" style={{ color: '#b86fec', display: 'block', fontSize: '1rem' }}>Token Decimals:</label>
                      <button onClick={() => setShowDecimalsInfo(true)} style={{ backgroundColor: '#b86fec', color: '#fff', border: 'none', borderRadius: '50%', width: '40px', height: '40px', fontSize: '1.2rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FiInfo /></button>
                    </div>
                    <input type="number" id="decimals" name="decimals" placeholder="Token Decimals (e.g., 18)" value={formData.decimals || 18} onChange={handleChange} required style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #b86fec', fontSize: '1rem', marginBottom: '10px' }} />
                    {showDecimalsInfo && <TokenDecimalsInfo setShowDecimalsInfo={setShowDecimalsInfo} />}
                  </div>
                )}
                {formData.rewardTokenSubPackage === 'Advanced Token Package' && (
                  <div style={{ marginTop: '10px', border: '1px solid #b86fec', borderRadius: '8px', padding: '10px' }}>
                    <strong style={{ color: '#b86fec' }}>Advanced Token Package Features:</strong>
                    <ul style={{ color: 'white', marginTop: '8px' }}>
                      <li>All Basic Package features</li>
                      <li>Mint function</li>
                      <li>Burn function</li>
                      <li>Transfer tax (e.g., for dev/marketing/charity)</li>
                      <li>Anti-whale (max tx / wallet limit)</li>
                      <li>Pause/unpause trading</li>
                      <li>Owner-only controls</li>
                    </ul>
                    {/* Advanced Token Customization Fields */}
                    <label htmlFor="tokenname" style={{ color: '#b86fec', marginBottom: '5px', display: 'block' }}>Token Name:</label>
                    <input type="text" id="tokenname" name="tokenname" placeholder="Token Name" value={formData.tokenname || 'MyToken'} onChange={handleChange} required style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #b86fec', fontSize: '1rem', marginBottom: '10px' }} />
                    <label htmlFor="symbol" style={{ color: '#b86fec', marginBottom: '5px', display: 'block' }}>Token Symbol:</label>
                    <input type="text" id="symbol" name="symbol" placeholder="Token Symbol" value={formData.symbol || 'MTK'} onChange={handleChange} required style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #b86fec', fontSize: '1rem', marginBottom: '10px' }} />
                    <label htmlFor="supply" style={{ color: '#b86fec', marginBottom: '5px', display: 'block' }}>Token Supply:</label>
                    <input type="number" id="supply" name="supply" placeholder="Token Supply" value={formData.supply || 1000000} onChange={handleChange} required style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #b86fec', fontSize: '1rem', marginBottom: '10px' }} />
                    <label htmlFor="decimals" style={{ color: '#b86fec', marginBottom: '5px', display: 'block' }}>Token Decimals:</label>
                    <input type="number" id="decimals" name="decimals" placeholder="Token Decimals (e.g., 18)" value={formData.decimals || 18} onChange={handleChange} required style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #b86fec', fontSize: '1rem', marginBottom: '10px' }} />
                    {/* Tax Percentage and Crypto Address Fields */}
                    {taxFields.map((field, index) => (
                      <div key={index} style={{ display: 'flex', gap: '20px', alignItems: 'flex-start', marginBottom: '10px' }}>
                        <div style={{ flex: '0 0 20%' }}>
                          <label htmlFor={field.id} style={{ color: '#b86fec', marginBottom: '5px', display: 'block' }}><span style={{ color: '#b86fec', marginRight: '5px' }}>•</span> {field.label}</label>
                          <input type="number" id={field.id} name={field.name} placeholder={field.placeholder} value={formData[field.name] || field.defaultValue} onChange={handleChange} required style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #b86fec', fontSize: '1rem' }} />
                        </div>
                        <div style={{ flex: '0 0 80%' }}>
                          <label htmlFor={field.cryptoId} style={{ color: '#b86fec', marginBottom: '5px', display: 'block' }}><span style={{ color: '#b86fec', marginRight: '5px' }}>•</span> {field.cryptoLabel}</label>
                          <input type="text" id={field.cryptoId} name={field.cryptoName} placeholder={field.cryptoPlaceholder} value={formData[field.cryptoName] || ''} onChange={handleChange} required style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #b86fec', fontSize: '1rem' }} />
                        </div>
                      </div>
                    ))}
                    <label htmlFor="transactionLimit" style={{ color: '#b86fec', marginBottom: '5px', display: 'block' }}>Transaction Limit:</label>
                    <input type="text" id="transactionLimit" name="transactionLimit" placeholder="Transaction Limit" value={formData.transactionLimit || '10000'} onChange={handleChange} required style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #b86fec', fontSize: '1rem' }} />
                    <label htmlFor="walletLimit" style={{ color: '#b86fec', marginBottom: '5px', display: 'block' }}>Wallet Limit:</label>
                    <input type="text" id="walletLimit" name="walletLimit" placeholder="Wallet Limit" value={formData.walletLimit || '100000'} onChange={handleChange} required style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #b86fec', fontSize: '1rem' }} />
                  </div>
                )}
                {/* Decimals Info Popup */}
                {showDecimalsInfo && <TokenDecimalsInfo setShowDecimalsInfo={setShowDecimalsInfo} />}
              </div>
            )}
            {formData.package === 'Basic Token Package' && (
              <div>
                <label htmlFor="name" style={{ color: '#b86fec', marginBottom: '5px', display: 'block' }}>
                  <span style={{ color: '#b86fec', marginRight: '5px' }}>•</span> Token Name:
                </label>
                <input
                  type="text"
                  id="tokenname"
                  name="tokenname"
                  placeholder="Token Name"
                  value={formData.tokenname || 'MyToken'} // Default value
                  onChange={handleChange}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    border: '1px solid #b86fec',
                    fontSize: '1rem',
                    marginBottom: '10px',
                  }}
                />
                <label htmlFor="symbol" style={{ color: '#b86fec', marginBottom: '5px', display: 'block' }}>
                  <span style={{ color: '#b86fec', marginRight: '5px' }}>•</span> Token Symbol:
                </label>
                <input
                  type="text"
                  id="symbol"
                  name="symbol"
                  placeholder="Token Symbol"
                  value={formData.symbol || 'MTK'} // Default value
                  onChange={handleChange}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    border: '1px solid #b86fec',
                    fontSize: '1rem',
                    marginBottom: '10px',
                  }}
                />
                <label htmlFor="supply" style={{ color: '#b86fec', marginBottom: '5px', display: 'block' }}>
                  <span style={{ color: '#b86fec', marginRight: '5px' }}>•</span> Token Supply:
                </label>
                <input
                  type="number"
                  id="supply"
                  name="supply"
                  placeholder="Token Supply"
                  value={formData.supply || 1000000} // Default value
                  onChange={handleChange}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    border: '1px solid #b86fec',
                    fontSize: '1rem',
                    marginBottom: '10px',
                  }}
                />
                {/* Token Decimals */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                <label htmlFor="decimals" style={{ color: '#b86fec', display: 'block', fontSize: '1rem' }}>
                  <span style={{ color: '#b86fec', marginRight: '5px' }}>•</span> Token Decimals:
                </label>
                {/* Button to show decimals info */}
                <button
                  onClick={() => setShowDecimalsInfo(true)} // Show the info popup
                  style={{
                    backgroundColor: '#b86fec',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '50%', // Makes the button circular
                    width: '40px', // Sets width for the circular button
                    height: '40px', // Sets height for the circular button
                    fontSize: '1.2rem', // Slightly larger font for the "i"
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <FiInfo /> {/* Render the info icon */}
                </button>
              </div>
                
                <input
                  type="number"
                  id="decimals"
                  name="decimals"
                  placeholder="Token Decimals (e.g., 18)"
                  value={formData.decimals || 18} // Default value
                  onChange={handleChange}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    border: '1px solid #b86fec',
                    fontSize: '1rem',
                    marginBottom: '10px',
                  }}
                />
                {/* Decimals Info Popup */}
                {showDecimalsInfo && <TokenDecimalsInfo setShowDecimalsInfo={setShowDecimalsInfo} />}
              </div>
            )}
            {formData.package === 'Advanced Token Package' && (
              <div>
                
                <label htmlFor="name" style={{ color: '#b86fec', marginBottom: '5px', display: 'block' }}>
                  <span style={{ color: '#b86fec', marginRight: '5px' }}>•</span> Token Name:
                </label>
                <input
                  type="text"
                  id="tokenname"
                  name="tokenname"
                  placeholder="Token Name"
                  value={formData.tokenname || 'MyToken'} // Default value
                  onChange={handleChange}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    border: '1px solid #b86fec',
                    fontSize: '1rem',
                    marginBottom: '10px',
                  }}
                />
                <label htmlFor="symbol" style={{ color: '#b86fec', marginBottom: '5px', display: 'block' }}>
                  <span style={{ color: '#b86fec', marginRight: '5px' }}>•</span> Token Symbol:
                </label>
                <input
                  type="text"
                  id="symbol"
                  name="symbol"
                  placeholder="Token Symbol"
                  value={formData.symbol || 'MTK'} // Default value
                  onChange={handleChange}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    border: '1px solid #b86fec',
                    fontSize: '1rem',
                    marginBottom: '10px',
                  }}
                />
                <label htmlFor="supply" style={{ color: '#b86fec', marginBottom: 'px', display: 'block' }}>
                  <span style={{ color: '#b86fec', marginRight: '5px' }}>•</span> Token Supply:
                </label>
                <input
                  type="number"
                  id="supply"
                  name="supply"
                  placeholder="Token Supply"
                  value={formData.supply || 1000000} // Default value
                  onChange={handleChange}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    border: '1px solid #b86fec',
                    fontSize: '1rem',
                    marginBottom: '10px',
                  }}
                />
                {/* Token Decimals */}
                <label htmlFor="decimals" style={{ color: '#b86fec', marginBottom: '5px', display: 'block' }}>
                  <span style={{ color: '#b86fec', marginRight: '5px' }}>•</span> Token Decimals:
                </label>
                <input
                  type="number"
                  id="decimals"
                  name="decimals"
                  placeholder="Token Decimals (e.g., 18)"
                  value={formData.decimals || 18} // Default value
                  onChange={handleChange}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    border: '1px solid #b86fec',
                    fontSize: '1rem',
                    marginBottom: '10px',
                  }}
                />
                
                <br /><br />
                {/* Tax Percentage and Crypto Address Fields */}
                {taxFields.map((field, index) => (

                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      gap: '20px',
                      alignItems: 'flex-start',
                      marginBottom: '10px',
                    }}
                  >
                    {/* Tax Percentage */}
                    <div style={{ flex: '0 0 20%' }}>
                      <label
                        htmlFor={field.id}
                        style={{
                          color: '#b86fec',
                          marginBottom: '5px',
                          display: 'block',
                        }}
                      >
                        <span style={{ color: '#b86fec', marginRight: '5px' }}>•</span> {field.label}
                      </label>
                      <input
                        type="number"
                        id={field.id}
                        name={field.name}
                        placeholder={field.placeholder}
                        value={formData[field.name] || field.defaultValue} // Default value
                        onChange={handleChange}
                        required
                        style={{
                          width: '100%',
                          padding: '10px',
                          borderRadius: '8px',
                          border: '1px solid #b86fec',
                          fontSize: '1rem',
                        }}
                      />
                    </div>

                    {/* Crypto Address */}
                    <div style={{ flex: '0 0 80%' }}>
                      <label
                        htmlFor={field.cryptoId}
                        style={{
                          color: '#b86fec',
                          marginBottom: '5px',
                          display: 'block',
                        }}
                      >
                        <span style={{ color: '#b86fec', marginRight: '5px' }}>•</span> {field.cryptoLabel}
                      </label>
                      <input
                        type="text"
                        id={field.cryptoId}
                        name={field.cryptoName}
                        placeholder={field.cryptoPlaceholder}
                        value={formData[field.cryptoName] || ''} // Default value
                        onChange={handleChange}
                        required
                        style={{
                          width: '100%',
                          padding: '10px',
                          borderRadius: '8px',
                          border: '1px solid #b86fec',
                          fontSize: '1rem',
                        }}
                      />
                    </div>
                  </div>
                ))}

                <br />
                <label htmlFor="transactionLimit" style={{ color: '#b86fec', marginBottom: '5px', display: 'block' }}>
                  <span style={{ color: '#b86fec', marginRight: '5px' }}>•</span> Transaction Limit:
                </label>
                <input
                  type="text"
                  id="transactionLimit"
                  name="transactionLimit"
                  placeholder="Transaction Limit"
                  value={formData.transactionLimit || '10000'} // Default value
                  onChange={handleChange}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    border: '1px solid #b86fec',
                    fontSize: '1rem',
                  }}
                />
                <label htmlFor="walletLimit" style={{ color: '#b86fec', marginBottom: '5px', display: 'block' }}>
                  <span style={{ color: '#b86fec', marginRight: '5px' }}>•</span> Wallet Limit:
                </label>
                <input
                  type="text"
                  id="walletLimit"
                  name="walletLimit"
                  placeholder="Wallet Limit"
                  value={formData.walletLimit || '100000'} // Default value
                  onChange={handleChange}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    border: '1px solid #b86fec',
                    fontSize: '1rem',
                  }}
                />
              </div>
            )}
            {/* Decimals Info Popup */}
            {showDecimalsInfo && <TokenDecimalsInfo setShowDecimalsInfo={setShowDecimalsInfo} />}
          </div>

         {formData.package === 'Custom Token Package' && (
              <div>
                <label htmlFor="name" style={{ color: '#b86fec', marginBottom: '5px', display: 'block' }}>
                  <span style={{ color: '#b86fec', marginRight: '5px' }}>•</span> Token Name:
                </label>
                <input
                  type="text"
                  id="tokenname"
                  name="tokenname"
                  placeholder="Token Name"
                  value={formData.tokenname || 'MyToken'} // Default value
                  onChange={handleChange}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    border: '1px solid #b86fec',
                    fontSize: '1rem',
                    marginBottom: '10px',
                  }}
                />
                <label htmlFor="symbol" style={{ color: '#b86fec', marginBottom: '5px', display: 'block' }}>
                  <span style={{ color: '#b86fec', marginRight: '5px' }}>•</span> Token Symbol:
                </label>
                <input
                  type="text"
                  id="symbol"
                  name="symbol"
                  placeholder="Token Symbol"
                  value={formData.symbol || 'MTK'} // Default value
                  onChange={handleChange}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    border: '1px solid #b86fec',
                    fontSize: '1rem',
                    marginBottom: '10px',
                  }}
                />
                <label htmlFor="supply" style={{ color: '#b86fec', marginBottom: '5px', display: 'block' }}>
                  <span style={{ color: '#b86fec', marginRight: '5px' }}>•</span> Token Supply:
                </label>
                <input
                  type="number"
                  id="supply"
                  name="supply"
                  placeholder="Token Supply"
                  value={formData.supply || 1000000} // Default value
                  onChange={handleChange}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    border: '1px solid #b86fec',
                    fontSize: '1rem',
                    marginBottom: '10px',
                  }}
                />
                {/* Token Decimals */}
                <label htmlFor="decimals" style={{ color: '#b86fec', marginBottom: '5px', display: 'block' }}>
                  <span style={{ color: '#b86fec', marginRight: '5px' }}>•</span> Token Decimals:
                </label>
                <input
                  type="number"
                  id="decimals"
                  name="decimals"
                  placeholder="Token Decimals (e.g., 18)"
                  value={formData.decimals || 18} // Default value
                  onChange={handleChange}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    border: '1px solid #b86fec',
                    fontSize: '1rem',
                    marginBottom: '10px',
                  }}
                />
                {/* Custom Functions */}
                <br />
                <br />
                <div style={{ marginTop: '20px' }}>
                  <label style={{ color: '#b86fec', marginBottom: '10px', display: 'block', fontSize: '1.1rem' }}>
                    <span style={{ color: '#b86fec', marginRight: '5px' }}>•</span> Select Functions:
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                    {[
                      'Mint Function |+125€',
                      'Burn Function |+125€',
                      'Transfer Tax - Founder |+180€',
                      'Transfer Tax - Marketing |+180€',
                      'Transfer Tax - Charity |+180€',
                      'Anti-Whale - Max Transaction |+75€',
                      'Anti-Whale - Max Wallet |+75€',
                      'Burn Enable Switch |+100€',
                      'Blacklist / Anti-Bot |+200€',
                      'Multi-ownership |+200€',
                      'Trading Enable Switch |+100€',
                      'Airdrop Redistribution to Whitelisted Holders |+350€',
                    ].map((functionName, index) => (
                      <div key={index} style={{ flex: '0 0 30%' }}>
                        <input
                          type="checkbox"
                          id={`function-${index}`}
                          name="functions"
                          value={functionName}
                          checked={formData.functions?.some(f => f.includes(functionName)) || false}
                          onChange={(e) => {
                            const { checked, value } = e.target;
                            setFormData((prevData) => ({
                              ...prevData,
                              functions: checked
                                ? [...(prevData.functions || []), value]
                                : prevData.functions.filter((func) => func !== value),
                            }));
                          }}
                          style={{
                            marginRight: '5px',
                          }}
                        />
                        <label htmlFor={`function-${index}`} style={{ color: 'white', fontSize: '0.9rem' }}>
                          {functionName}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <br />
                {/* Conditionally Render based on selected function */}
                {formData.functions?.some(f => f.includes('Mint Function')) && (
                <>
                <br />
                <br />
                  <label
                  style={{
                    color: "#b86fec",
                    marginBottom: "15px", // Increased spacing for better separation
                    display: "block",
                    fontSize: "1.1rem", // Slightly larger font for emphasis
                    lineHeight: "1.6", // Improved line spacing for readability
                    textAlign: "left", // Align text to the left for a clean layout
                  }}
                >
                  <strong style={{ fontWeight: "bold", color: "#b86fec", textDecoration: "underline" }}>Mint Mechanism: </strong>
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "5px", // Space between the icon and text
                      color: "white",
                      fontSize: "0.95rem",
                    }}
                  >
                    {/* Information Icon */}
                    <span
                      style={{
                        display: "inline-block",
                        width: "20px",
                        height: "20px",
                        borderRadius: "50%",
                        backgroundColor: "#b86fec", // Purple background for the icon
                        color: "white",
                        fontSize: "0.85rem",
                        fontWeight: "bold",
                        textAlign: "center",
                        lineHeight: "20px", // Center the text inside the circle
                      }}
                    >
                      i
                    </span>
                    {/* Information Text */}
                    <span>
                    allows you to create (or "mint") a new supply of tokens.
                    </span>
                  </span>
                </label>
                </>
                )}
                {formData.functions?.some(f => f.includes('Burn Function')) && (
                  <>
                  <br />
                  <br />
                  <label
                  style={{
                    color: "#b86fec",
                    marginBottom: "15px",
                    display: "block",
                    fontSize: "1.1rem",
                    lineHeight: "1.6",
                    textAlign: "left",
                  }}
                >
                  <strong style={{ fontWeight: "bold", color: "#b86fec", textDecoration: "underline" }}>Burn Mechanism: </strong>
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                      color: "white",
                      fontSize: "0.95rem",
                    }}
                  >
                    {/* Information Icon */}
                    <span
                      style={{
                        display: "inline-block",
                        width: "20px",
                        height: "20px",
                        borderRadius: "50%",
                        backgroundColor: "#b86fec",
                        color: "white",
                        fontSize: "0.85rem",
                        fontWeight: "bold",
                        textAlign: "center",
                        lineHeight: "20px",
                      }}
                    >
                      i
                    </span>
                    {/* Information Text */}
                    <span>
                    permanently removes a specified amount of tokens from circulation.
                    </span>
                  </span>
                </label>
                  </>
                )}
                
                
                
                {formData.functions?.some(f => f.includes('Transfer Tax - Founder')) && (
                  <div style={{ marginTop: '20px' }}>
                    <br />
                    <br />
                    <label
                  style={{
                    color: "#b86fec",
                    marginBottom: "15px", // Increased spacing for better separation
                    display: "block",
                    fontSize: "1.1rem", // Slightly larger font for emphasis
                    lineHeight: "1.6", // Improved line spacing for readability
                    textAlign: "left", // Align text to the left for a clean layout
                  }}
                >
                  <strong style={{ fontWeight: "bold", color: "#b86fec", textDecoration: "underline" }}>Transfer tax ( Founder ): </strong>
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "5px", // Space between the icon and text
                      color: "white",
                      fontSize: "0.95rem",
                    }}
                  >
                    {/* Information Icon */}
                    <span
                      style={{
                        display: "inline-block",
                        width: "20px",
                        height: "20px",
                        borderRadius: "50%",
                        backgroundColor: "#b86fec", // Purple background for the icon
                        color: "white",
                        fontSize: "0.85rem",
                        fontWeight: "bold",
                        textAlign: "center",
                        lineHeight: "20px", // Center the text inside the circle
                      }}
                    >
                      i
                    </span>
                    {/* Information Text */}
                    <span>
                    This mechanism allows the smart contract to deduct a percentage of each transaction (e.g., token transfers) and allocate it to the founder's wallet.                    </span>
                  </span>
                </label>
                    {[
                      {
                        id: 'taxPercentageDev',
                        name: 'taxPercentageDev',
                        label: 'Tax %:',
                        placeholder: 'Tax Percentage',
                        defaultValue: 2,
                        cryptoLabel: 'Founder Crypto Address:',
                        cryptoId: 'cryptoAddressDev',
                        cryptoName: 'cryptoAddressDev',
                        cryptoPlaceholder: 'Founder Crypto Address',
                      },
                    ].map((field, index) => (
                      <div
                        key={index}
                        style={{
                          display: 'flex',
                          gap: '20px',
                          alignItems: 'flex-start',
                          marginBottom: '10px',
                        }}
                      >
                        {/* Tax Percentage */}
                        <div style={{ flex: '0 0 20%' }}>
                          <label
                            htmlFor={field.id}
                            style={{
                              color: '#b86fec',
                              marginBottom: '5px',
                              display: 'block',
                            }}
                            >
                            <span style={{ color: '#b86fec', marginRight: '5px' }}>•</span> {field.label}
                          </label>
                          <input
                            type="number"
                            id={field.id}
                            name={field.name}
                            placeholder={field.placeholder}
                            value={formData[field.name] || field.defaultValue} // Default value
                            onChange={handleChange}
                            required
                            style={{
                              width: '100%',
                              padding: '10px',
                              borderRadius: '8px',
                              border: '1px solid #b86fec',
                              fontSize: '1rem',
                            }}
                          />
                        </div>
                        {/* Crypto Address */}
                        <div style={{ flex: '0 0 80%' }}>
                          <label
                            htmlFor={field.cryptoId}
                            style={{
                              color: '#b86fec',
                              marginBottom: '5px',
                              display: 'block',
                            }}
                          >
                            <span style={{ color: '#b86fec', marginRight: '5px' }}>•</span> {field.cryptoLabel}
                          </label>
                          <input
                            type="text"
                            id={field.cryptoId}
                            name={field.cryptoName}
                            placeholder={field.cryptoPlaceholder}
                            value={formData[field.cryptoName] || ''} // Default value
                            onChange={handleChange}
                            required
                            style={{
                              width: '100%',
                              padding: '10px',
                              borderRadius: '8px',
                              border: '1px solid #b86fec',
                              fontSize: '1rem',
                            }}
                          />
                          <br />
                        </div>
                        </div>
                    ))}
                  </div>
                )}
                  {formData.functions?.some(f => f.includes('Transfer Tax - Marketing')) && (
                  <div style={{ marginTop: '20px' }}>
                    <br />
                    <br />
                    <label
                  style={{
                    color: "#b86fec",
                    marginBottom: "15px", // Increased spacing for better separation
                    display: "block",
                    fontSize: "1.1rem", // Slightly larger font for emphasis
                    lineHeight: "1.6", // Improved line spacing for readability
                    textAlign: "left", // Align text to the left for a clean layout
                  }}
                >
                  <strong style={{ fontWeight: "bold", color: "#b86fec", textDecoration: "underline" }}>Transfer tax ( Marketing ): </strong>
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "5px", // Space between the icon and text
                      color: "white",
                      fontSize: "0.95rem",
                    }}
                  >
                    {/* Information Icon */}
                    <span
                      style={{
                        display: "inline-block",
                        width: "20px",
                        height: "20px",
                        borderRadius: "50%",
                        backgroundColor: "#b86fec", // Purple background for the icon
                        color: "white",
                        fontSize: "0.85rem",
                        fontWeight: "bold",
                        textAlign: "center",
                        lineHeight: "20px", // Center the text inside the circle
                      }}
                    >
                      i
                    </span>
                    {/* Information Text */}
                    <span>
                    This mechanism allows the smart contract to deduct a percentage of each transaction (e.g., token transfers) and allocate it to the marketing's wallet.                    </span>
                  </span>
                </label>
                    {[
                      {
                        id: 'taxPercentageMark',
                        name: 'taxPercentageMark',
                        label: 'Tax %:',
                        placeholder: 'Tax Percentage',
                        defaultValue: 6,
                        cryptoLabel: 'Marketing Crypto Address:',
                        cryptoId: 'cryptoAddressMark',
                        cryptoName: 'cryptoAddressMark',
                        cryptoPlaceholder: 'Marketing Crypto Address',
                      },
                    ].map((field, index) => (
                      <div
                        key={index}
                        style={{
                          display: 'flex',
                          gap: '20px',
                          alignItems: 'flex-start',
                          marginBottom: '10px',
                        }}
                      >
                        {/* Tax Percentage */}
                        <div style={{ flex: '0 0 20%' }}>
                          <label
                            htmlFor={field.id}
                            style={{
                              color: '#b86fec',
                              marginBottom: '5px',
                              display: 'block',
                            }}
                            >
                            <span style={{ color: '#b86fec', marginRight: '5px' }}>•</span> {field.label}
                          </label>
                          <input
                            type="number"
                            id={field.id}
                            name={field.name}
                            placeholder={field.placeholder}
                            value={formData[field.name] || field.defaultValue} // Default value
                            onChange={handleChange}
                            required
                            style={{
                              width: '100%',
                              padding: '10px',
                              borderRadius: '8px',
                              border: '1px solid #b86fec',
                              fontSize: '1rem',
                            }}
                          />
                        </div>
                        {/* Crypto Address */}
                        <div style={{ flex: '0 0 80%' }}>
                          <label
                            htmlFor={field.cryptoId}
                            style={{
                              color: '#b86fec',
                              marginBottom: '5px',
                              display: 'block',
                            }}
                          >
                            <span style={{ color: '#b86fec', marginRight: '5px' }}>•</span> {field.cryptoLabel}
                          </label>
                          <input
                            type="text"
                            id={field.cryptoId}
                            name={field.cryptoName}
                            placeholder={field.cryptoPlaceholder}
                            value={formData[field.cryptoName] || ''} // Default value
                            onChange={handleChange}
                            required
                            style={{
                              width: '100%',
                              padding: '10px',
                              borderRadius: '8px',
                              border: '1px solid #b86fec',
                              fontSize: '1rem',
                            }}
                          />
                          <br />
                        </div>
                        </div>
                    ))}
                  </div>
                )}
                {formData.functions?.some(f => f.includes('Transfer Tax - Charity')) && (
                  <div style={{ marginTop: '20px' }}>
                    <br />
                    <br />
                    <label
                  style={{
                    color: "#b86fec",
                    marginBottom: "15px", // Increased spacing for better separation
                    display: "block",
                    fontSize: "1.1rem", // Slightly larger font for emphasis
                    lineHeight: "1.6", // Improved line spacing for readability
                    textAlign: "left", // Align text to the left for a clean layout
                  }}
                >
                  <strong style={{ fontWeight: "bold", color: "#b86fec", textDecoration: "underline" }}>Transfer tax ( Charity ): </strong>
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "5px", // Space between the icon and text
                      color: "white",
                      fontSize: "0.95rem",
                    }}
                  >
                    {/* Information Icon */}
                    <span
                      style={{
                        display: "inline-block",
                        width: "20px",
                        height: "20px",
                        borderRadius: "50%",
                        backgroundColor: "#b86fec", // Purple background for the icon
                        color: "white",
                        fontSize: "0.85rem",
                        fontWeight: "bold",
                        textAlign: "center",
                        lineHeight: "20px", // Center the text inside the circle
                      }}
                    >
                      i
                    </span>
                    {/* Information Text */}
                    <span>
                    This mechanism allows the smart contract to deduct a percentage of each transaction (e.g., token transfers) and allocate it to the charity's wallet.                    </span>
                  </span>
                </label>
                    {[
                      {
                        id: 'taxPercentageChar',
                        name: 'taxPercentageChar',
                        label: 'Tax %:',
                        placeholder: 'Tax Percentage',
                        defaultValue: 6,
                        cryptoLabel: 'Charity Crypto Address:',
                        cryptoId: 'cryptoAddressChar',
                        cryptoName: 'cryptoAddressChar',
                        cryptoPlaceholder: 'Charity Crypto Address',
                      },
                    ].map((field, index) => (
                      <div
                        key={index}
                        style={{
                          display: 'flex',
                          gap: '20px',
                          alignItems: 'flex-start',
                          marginBottom: '10px',
                        }}
                      >
                        {/* Tax Percentage */}
                        <div style={{ flex: '0 0 20%' }}>
                          <label
                            htmlFor={field.id}
                            style={{
                              color: '#b86fec',
                              marginBottom: '5px',
                              display: 'block',
                            }}
                            >
                            <span style={{ color: '#b86fec', marginRight: '5px' }}>•</span> {field.label}
                          </label>
                          <input
                            type="number"
                            id={field.id}
                            name={field.name}
                            placeholder={field.placeholder}
                            value={formData[field.name] || field.defaultValue} // Default value
                            onChange={handleChange}
                            required
                            style={{
                              width: '100%',
                              padding: '10px',
                              borderRadius: '8px',
                              border: '1px solid #b86fec',
                              fontSize: '1rem',
                            }}
                          />
                        </div>
                        {/* Crypto Address */}
                        <div style={{ flex: '0 0 80%' }}>
                          <label
                            htmlFor={field.cryptoId}
                            style={{
                              color: '#b86fec',
                              marginBottom: '5px',
                              display: 'block',
                            }}
                          >
                            <span style={{ color: '#b86fec', marginRight: '5px' }}>•</span> {field.cryptoLabel}
                          </label>
                          <input
                            type="text"
                            id={field.cryptoId}
                            name={field.cryptoName}
                            placeholder={field.cryptoPlaceholder}
                            value={formData[field.cryptoName] || ''} // Default value
                            onChange={handleChange}
                            required
                            style={{
                              width: '100%',
                              padding: '10px',
                              borderRadius: '8px',
                              border: '1px solid #b86fec',
                              fontSize: '1rem',
                            }}
                          />
                          <br />
                        </div>
                        </div>
                    ))}
                  </div>
                )}


                {formData.functions?.some(f => f.includes('Anti-Whale - Max Transaction')) && (
                  <>
                  <br />
                  <br />
                  <label
                  style={{
                    color: "#b86fec",
                    marginBottom: "15px", // Increased spacing for better separation
                    display: "block",
                    fontSize: "1.1rem", // Slightly larger font for emphasis
                    lineHeight: "1.6", // Improved line spacing for readability
                    textAlign: "left", // Align text to the left for a clean layout
                  }}
                >
                  <strong style={{ fontWeight: "bold", color: "#b86fec", textDecoration: "underline" }}>Maximum Transaction Limit: </strong>
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "5px", // Space between the icon and text
                      color: "white",
                      fontSize: "0.95rem",
                    }}
                  >
                    {/* Information Icon */}
                    <span
                      style={{
                        display: "inline-block",
                        width: "20px",
                        height: "20px",
                        borderRadius: "50%",
                        backgroundColor: "#b86fec", // Purple background for the icon
                        color: "white",
                        fontSize: "0.85rem",
                        fontWeight: "bold",
                        textAlign: "center",
                        lineHeight: "20px", // Center the text inside the circle
                      }}
                    >
                      i
                    </span>
                    {/* Information Text */}
                    <span>
                    Restricts the size of individual transactions to prevent excessively large transfers, often used as an anti-bot measure.                    </span>
                  </span>
                </label>
                  <label htmlFor="transactionLimit" style={{ color: '#b86fec', marginBottom: '5px', display: 'block' }}>
                  <span style={{ color: '#b86fec', marginRight: '5px' }}>•</span> Transaction Limit:
                </label>
                <input
                  type="text"
                  id="transactionLimit"
                  name="transactionLimit"
                  placeholder="Transaction Limit"
                  value={formData.transactionLimit || '10000'} // Default value
                  onChange={handleChange}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    border: '1px solid #b86fec',
                    fontSize: '1rem',
                  }}
                /><br />
                </>
                )}
                {formData.functions?.some(f => f.includes('Anti-Whale - Max Wallet')) && (

                  <>
                  <br />
                  <br />
                    <label
                  style={{
                    color: "#b86fec",
                    marginBottom: "15px", // Increased spacing for better separation
                    display: "block",
                    fontSize: "1.1rem", // Slightly larger font for emphasis
                    lineHeight: "1.6", // Improved line spacing for readability
                    textAlign: "left", // Align text to the left for a clean layout
                  }}
                >
                  <strong style={{ fontWeight: "bold", color: "#b86fec", textDecoration: "underline" }}>Maximum Wallet Limit (Anti-Whale): </strong>
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "5px", // Space between the icon and text
                      color: "white",
                      fontSize: "0.95rem",
                    }}
                  >
                    {/* Information Icon */}
                    <span
                      style={{
                        display: "inline-block",
                        width: "20px",
                        height: "20px",
                        borderRadius: "50%",
                        backgroundColor: "#b86fec", // Purple background for the icon
                        color: "white",
                        fontSize: "0.85rem",
                        fontWeight: "bold",
                        textAlign: "center",
                        lineHeight: "20px", // Center the text inside the circle
                      }}
                    >
                      i
                    </span>
                    {/* Information Text */}
                    <span>
                    Restricts a wallet from holding more than a specified percentage of the total token supply to prevent disproportionate ownership.
                    </span>
                  </span>
                </label>
                <label htmlFor="walletLimit" style={{ color: '#b86fec', marginBottom: '5px', display: 'block' }}>
                  <span style={{ color: '#b86fec', marginRight: '5px' }}>•</span> Wallet Limit:
                </label>
                <input
                  type="text"
                  id="walletLimit"
                  name="walletLimit"
                  placeholder="Wallet Limit"
                  value={formData.walletLimit || '100000'} // Default value
                  onChange={handleChange}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    border: '1px solid #b86fec',
                    fontSize: '1rem',
                  }}
                /> <br />
                </>
                )}


                <br />
                {formData.functions?.some(f => f.includes('Burn Enable Switch')) && (
                  <>
                  <br />
                  <br />
                  <label
                  style={{
                    color: "#b86fec",
                    marginBottom: "15px", // Increased spacing for better separation
                    display: "block",
                    fontSize: "1.1rem", // Slightly larger font for emphasis
                    lineHeight: "1.6", // Improved line spacing for readability
                    textAlign: "left", // Align text to the left for a clean layout
                  }}
                >
                  <strong style={{ fontWeight: "bold", color: "#b86fec", textDecoration: "underline" }}>Burn Enable Switch: </strong>
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "5px", // Space between the icon and text
                      color: "white",
                      fontSize: "0.95rem",
                    }}
                  >
                    {/* Information Icon */}
                    <span
                      style={{
                        display: "inline-block",
                        width: "20px",
                        height: "20px",
                        borderRadius: "50%",
                        backgroundColor: "#b86fec", // Purple background for the icon
                        color: "white",
                        fontSize: "0.85rem",
                        fontWeight: "bold",
                        textAlign: "center",
                        lineHeight: "20px", // Center the text inside the circle
                      }}
                    >
                      i
                    </span>
                    {/* Information Text */}
                    <span>
                    is a feature that allows the contract owner to toggle the burn functionality on or off. When enabled, users can permanently remove tokens from circulation by using the burn function. When disabled, the burn function becomes inaccessible, preventing token removal.                    </span>
                  </span>
                </label>
                  </>
                )}

                {formData.functions?.some(f => f.includes('Trading Enable Switch')) && (
                  <>
                  <br />
                  <br />
                  <label
                      style={{
                        color: "#b86fec",
                        marginBottom: "15px", // Increased spacing for better separation
                        display: "block",
                        fontSize: "1.1rem", // Slightly larger font for emphasis
                        lineHeight: "1.6", // Improved line spacing for readability
                        textAlign: "left", // Align text to the left for a clean layout
                      }}
                    >
                      <strong style={{ fontWeight: "bold", color: "#b86fec", textDecoration: "underline" }}>Trading Enable Switch: </strong>
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "5px", // Space between the icon and text
                          color: "white",
                          fontSize: "0.95rem",
                        }}
                      >
                        {/* Information Icon */}
                        <span
                          style={{
                            display: "inline-block",
                            width: "20px",
                            height: "20px",
                            borderRadius: "50%",
                            backgroundColor: "#b86fec", // Purple background for the icon
                            color: "white",
                            fontSize: "0.85rem",
                            fontWeight: "bold",
                            textAlign: "center",
                            lineHeight: "20px", // Center the text inside the circle
                          }}
                        >
                          i
                        </span>
                        {/* Information Text */}
                        <span>
                        is a feature that allows the contract owner to pause or unpause token trading. When trading is paused, all token transfers are temporarily disabled, preventing transactions. When trading is unpaused, transfers resume as normal.                         </span>
                      </span>
                    </label>
                  </>
                )}

                {formData.functions?.some(f => f.includes('Blacklist / Anti-Bot')) && (
                  <>
                    <br />
                    <br />
                    <label
                      style={{
                        color: "#b86fec",
                        marginBottom: "15px", // Increased spacing for better separation
                        display: "block",
                        fontSize: "1.1rem", // Slightly larger font for emphasis
                        lineHeight: "1.6", // Improved line spacing for readability
                        textAlign: "left", // Align text to the left for a clean layout
                      }}
                    >
                      <strong style={{ fontWeight: "bold", color: "#b86fec", textDecoration: "underline" }}>Blacklist: </strong>
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "5px", // Space between the icon and text
                          color: "white",
                          fontSize: "0.95rem",
                        }}
                      >
                        {/* Information Icon */}
                        <span
                          style={{
                            display: "inline-block",
                            width: "20px",
                            height: "20px",
                            borderRadius: "50%",
                            backgroundColor: "#b86fec", // Purple background for the icon
                            color: "white",
                            fontSize: "0.85rem",
                            fontWeight: "bold",
                            textAlign: "center",
                            lineHeight: "20px", // Center the text inside the circle
                          }}
                        >
                          i
                        </span>
                        {/* Information Text */}
                        <span>
                        in trading is a feature that prevents specific wallet addresses from participating in token transfers. Wallets on the blacklist are restricted from sending or receiving tokens, effectively blocking them from trading.
                        </span>
                      </span>
                    </label>
                    <br />
                    <label
                      htmlFor="blacklistAddresses"
                      style={{ color: '#b86fec', marginBottom: '10px', display: 'block', fontSize: '1.1rem' }}
                    >
                     Blacklist Crypto Addresses:
                    </label>
                    <textarea
                      id="blacklistAddresses"
                      name="blacklistAddresses"
                      placeholder="Enter crypto addresses to blacklist, separated by commas. Can also be left empty if you want to add them later."
                      value={formData.blacklistAddresses || ''} // Default value
                      onChange={handleChange}
                      rows="4"
                      style={{
                        width: '100%',
                        padding: '10px',
                        borderRadius: '8px',
                        border: '1px solid #b86fec',
                        fontSize: '1rem',
                        resize: 'none',
                      }}
                    ></textarea>
                  </>
                )}

                  {formData.functions?.some(f => f.includes('Multi-ownership')) && (
                  <div style={{ textAlign: "left", width: "80%", marginBottom: "20px" }}>
                    <br />
                    <br />
                    <label
                      style={{
                        color: "#b86fec",
                        marginBottom: "15px", // Increased spacing for better separation
                        display: "block",
                        fontSize: "1.1rem", // Slightly larger font for emphasis
                        lineHeight: "1.6", // Improved line spacing for readability
                        textAlign: "left", // Align text to the left for a clean layout
                      }}
                    >
                      <strong style={{ fontWeight: "bold", color: "#b86fec", textDecoration: "underline" }}>Multi-ownership: </strong>
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "5px", // Space between the icon and text
                          color: "white",
                          fontSize: "0.95rem",
                        }}
                      >
                        {/* Information Icon */}
                        <span
                          style={{
                            display: "inline-block",
                            width: "20px",
                            height: "20px",
                            borderRadius: "50%",
                            backgroundColor: "#b86fec", // Purple background for the icon
                            color: "white",
                            fontSize: "0.85rem",
                            fontWeight: "bold",
                            textAlign: "center",
                            lineHeight: "20px", // Center the text inside the circle
                          }}
                        >
                          i
                        </span>
                        {/* Information Text */}
                        <span>
                        is a feature that allows multiple wallet addresses to share ownership of a smart contract. Instead of a single owner, multiple authorized addresses can manage and control the contract.
                        </span>
                      </span>
                    </label>
                    <br />
                    <label htmlFor="additionalOwners" style={{ color: '#b86fec', marginBottom: '10px', display: 'block', fontSize: '1.1rem' }}
                    >
                      <span style={{ color: "#b86fec", marginRight: "5px" }}>•</span> Add more owners - wallet addresses:
                    </label>
                    <textarea
                      id="additionalOwners"
                      name="additionalOwners"
                      placeholder="Enter crypto addresses to the owner list, separated by commas. Can also be left empty if you want to add them later."
                      rows="3"
                      value={formData.additionalOwners || ""} // Bind to form data
                      onChange={(e) => setFormData({ ...formData, additionalOwners: e.target.value })} // Update form data
                      style={{
                        width: "100%",
                        padding: "10px",
                        borderRadius: "8px",
                        border: "1px solid #b86fec",
                        fontSize: "1rem",
                        backgroundColor: "#fff",
                        color: "#333",
                      }}
                    />
                  </div>
                )}

                {formData.functions?.some(f => f.includes('Airdrop Redistribution to Whitelisted Holders')) && (
                <>
                  <br />
                  {/* Percentage Input */}
                    <br />
                    <br />
                    <label
                      style={{
                        color: "#b86fec",
                        marginBottom: "15px", // Increased spacing for better separation
                        display: "block",
                        fontSize: "1.1rem", // Slightly larger font for emphasis
                        lineHeight: "1.6", // Improved line spacing for readability
                        textAlign: "left", // Align text to the left for a clean layout
                      }}
                    >
                      <strong style={{ fontWeight: "bold", color: "#b86fec", textDecoration: "underline" }}>Airdrop Redistribution to Whitelist: </strong>
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "5px", // Space between the icon and text
                          color: "white",
                          fontSize: "0.95rem",
                        }}
                      >
                        {/* Information Icon */}
                        <span
                          style={{
                            display: "inline-block",
                            width: "20px",
                            height: "20px",
                            borderRadius: "50%",
                            backgroundColor: "#b86fec", // Purple background for the icon
                            color: "white",
                            fontSize: "0.85rem",
                            fontWeight: "bold",
                            textAlign: "center",
                            lineHeight: "20px", // Center the text inside the circle
                          }}
                        >
                          i
                        </span>
                        {/* Information Text */}
                        <span>
                          This feature allows tokens to be distributed equally among wallet addresses that are part of a predefined whitelist. Only the addresses on the whitelist will receive the tokens during the airdrop.
                        </span>
                      </span>
                    </label>
                  <label
                    htmlFor="redistributionPercentage"
                    name="redistributionPercentage"
                    style={{ color: '#b86fec', marginBottom: '10px', display: 'block', fontSize: '1.1rem' }}
                  >
                    <span style={{ color: '#b86fec', marginRight: '5px' }}>•</span> Redistribution Percentage:
                  </label>
                  <input
                    type="number"
                    id="redistributionPercentage"
                    name="redistributionPercentage"
                    placeholder="Enter percentage to redistribute"
                    value={formData.redistributionPercentage || ''} // Default value
                    onChange={handleChange}
                    required
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '8px',
                      border: '1px solid #b86fec',
                      fontSize: '1rem',
                      marginBottom: '10px',
                    }}
                  />

                  {/* List of Holders */}
                  <label
                    htmlFor="whitelistedHolders"
                    name="whitelistedHolders"
                    style={{ color: '#b86fec', marginBottom: '10px', display: 'block', fontSize: '1.1rem' }}
                  >
                    <span style={{ color: '#b86fec', marginRight: '5px' }}>•</span> Whitelisted Holders:
                  </label>
                  <textarea
                    id="whitelistedHolders"
                    name="whitelistedHolders"
                    placeholder="Enter a list of holders' crypto addresses, separated by commas. You can update this list later."
                    value={formData.whitelistedHolders || ''} // Default value
                    onChange={handleChange}
                    rows="4"
                    required
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '8px',
                      border: '1px solid #b86fec',
                      fontSize: '1rem',
                      resize: 'none',
                    }}
                  ></textarea> <br />
                </>
                )}

                <br />
                <br />
                <label htmlFor="customDetails" style={{ color: '#b86fec', marginBottom: '5px', display: 'block' }}>
                  Custom Requirements:
                </label>
                <textarea
                  id="customDetails"
                  name="customDetails"
                  placeholder="Describe your custom requirements"
                  rows="4"
                  value={formData.customDetails || 'Describe your custom requirements'} // Default value
                  onChange={handleChange}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    border: '1px solid #b86fec',
                    fontSize: '1rem',
                  }}
                />
              </div>
            )} 
        

        {/* NFT Drop Info Message */}
        {(formData.package === 'Single NFT Drop' || formData.package === 'Collection NFT Drop (Up to 50 NFTs)') && (
          <div style={{ margin: '40px 0', padding: '30px', background: '#f9f9ff', border: '2px solid #b86fec', borderRadius: '12px', color: '#333', textAlign: 'center', fontSize: '1.1rem' }}>
            <strong>To request an NFT/a Collection, please send your request directly via email.</strong>
            <br /><br />
            Please email us at <a href="mailto:cryptobartl_emb@outlook.com" style={{ color: '#b86fec', fontWeight: 'bold' }}>cryptobartl_emb@outlook.com</a> with the following information:
            <br /><br />
            <ul style={{ textAlign: 'left', margin: '20px auto', maxWidth: '500px', color: '#333' }}>
              <li><strong>Title: NFT Request</strong></li>
              <li><strong>Email content:</strong></li>
              <li>Your name</li>
              <li>Your email address</li>
              <li>Your crypto wallet address for delivery</li>
              <br />
              <li>The NFT image as an attachment (JPG, PNG, or GIF)</li>
              <li>All NFT metadata (name, description, attributes, etc.)</li>
              <br />
              <li>Any special wishes or requirements</li>
            </ul>
            <span style={{ color: '#b86fec', fontWeight: 'bold' }}>We cannot process NFT drop requests through this form. Please use email for all NFT drop requests.</span>
          </div>
        )}

        {!(formData.package === 'Single NFT Drop' || formData.package === 'Collection NFT Drop (Up to 50 NFTs)') &&
        (<>

          <br />
          {/* Sell for ETH */}
          <div style={{ textAlign: 'left', width: '80%', marginBottom: '20px' }}>
            <label htmlFor="sellForETH" style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>
              <input
                type="checkbox"
                id="sellForETH"
                name="sellForETH"
                checked={formData.sellForETH || false} // Bind to form data
                onChange={(e) => setFormData({ ...formData, sellForETH: e.target.checked })} // Update form data
                style={{
                  marginRight: '10px',
                  accentColor: '#b86fec', // Matches the theme
                  transform: 'scale(1.2)', // Slightly larger checkbox
                }}
              />
              Add option to sell for ETH (e.g. for a "Buy Now" button to your token page) - you get an additional proxy contract | 
              <span style={{ color: '#b86fec' }}>+ € 1800</span>

            </label>
          </div>
          
          {/* Conditional rendering for Initial Token Price */}
          {formData.sellForETH && (
            <div style={{ textAlign: 'left', width: '80%', marginBottom: '20px' }}>
              <label htmlFor="symbol" style={{ color: '#b86fec', marginBottom: '5px', display: 'block' }}>
                  <span style={{ color: '#b86fec', marginRight: '5px' }}>•</span> Initial Token Price in ETH:
                </label>
              <input
                type="number"
                id="initialTokenPrice"
                name="initialTokenPrice"
                placeholder="Enter initial price in ETH"
                value={formData.initialTokenPrice || ''} // Bind to form data
                onChange={(e) => setFormData({ ...formData, initialTokenPrice: e.target.value })} // Update form data
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '8px',
                  border: '1px solid #b86fec',
                  fontSize: '1rem',
                  backgroundColor: '#fff',
                  color: '#333',
                }}
              />

              <br /> <br />
              {/* Token Amount to be put to sale */}
              <label htmlFor="tokenAmountForSale" style={{ color: '#b86fec', marginBottom: '5px', display: 'block' }}>
                <span style={{ color: '#b86fec', marginRight: '5px' }}>•</span> Token Amount to be put to sale: (no decimals)
              </label>
              <input
                type="number"
                id="tokenAmountForSale"
                name="tokenAmountForSale"
                placeholder={formData.supply}
                value={formData.tokenAmountForSale || ''} // Bind to form data
                onChange={(e) => setFormData({ ...formData, tokenAmountForSale: e.target.value })} // Update form data
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '8px',
                  border: '1px solid #b86fec',
                  fontSize: '1rem',
                  backgroundColor: '#fff',
                  color: '#333',
                }}
              />
              <br />
              <br />
              {/* Payment Earnings Method */}
              <div style={{ textAlign: "left", width: "80%", marginBottom: "20px" }}>
              <label htmlFor="paymentMethod" style={{ color: '#b86fec', marginBottom: '5px', display: 'block' }}>
                <span style={{ color: '#b86fec', marginRight: '5px' }}>•</span> Payment Earnings Method:
              </label>
                <select
                  id="paymentMethod"
                  name="paymentMethod"
                  value={formData.paymentMethod || ""} // Default to empty value
                  onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })} // Update form data
                  style={{
                    width: "100%",
                    padding: "10px",
                    borderRadius: "8px",
                    border: "1px solid #b86fec",
                    fontSize: "1rem",
                    backgroundColor: "#fff",
                    color: "#333",
                  }}
                >
                  <option value="" disabled>
                    -- Choose one --
                  </option>
                  <option value="sendETH">Send the ETH directly to address: -</option>
                  <option value="collectETH">Collect the ETH in the contract, withdraw on request</option>
                </select>
              </div>
              
              {/* Conditional rendering for Collector Address */}
              {formData.paymentMethod === "sendETH" && (
                <div style={{ textAlign: "left", width: "80%", marginBottom: "20px" }}>
                  <label htmlFor="collectorAddress" style={{ color: '#b86fec', marginBottom: '5px', display: 'block' }}>
                    <span style={{ color: '#b86fec', marginRight: '5px' }}>•</span> Collector Crypto Wallet Address:
                  </label>
                  <input
                    type="text"
                    id="collectorAddress"
                    name="collectorAddress"
                    placeholder={formData.cryptoWallet}
                    value={formData.collectorAddress || ""} // Bind to form data
                    onChange={(e) => setFormData({ ...formData, collectorAddress: e.target.value })} // Update form data
                    style={{
                      width: "100%",
                      padding: "10px",
                      borderRadius: "8px",
                      border: "1px solid #b86fec",
                      fontSize: "1rem",
                      backgroundColor: "#fff",
                      color: "#333",
                    }}
                  />
                </div>
              )}
            </div>
          )}

          <br />
          {/* Additional Information */}
          <div style={{ textAlign: 'left', width: '80%' }}>
            <label htmlFor="additionalInfo" style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>
              Additional Information:
            </label>
            <textarea
              id="additionalInfo"
              name="additionalInfo"
              placeholder="Your message to us (e.g., company, industry, and special requests)"
              rows="4"
              value={formData.additionalInfo}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '10px',
                borderRadius: '8px',
                border: '1px solid #b86fec',
                fontSize: '1rem',
              }}
            />
          </div>
          <br />
          <br />

          {/* Promotion Options */}
          <div style={{ textAlign: 'left', width: '80%', marginTop: '20px' }}>
            <label style={{ color: '#b86fec', marginBottom: '15px', display: 'block', fontSize: '1.2rem', fontWeight: 'bold' }}>
              Promotion Options
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', padding: '10px', backgroundColor: '#f9f9f9', borderRadius: '8px', boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)' }}>
              {promotionOptions.map((option, index) => (
                <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <input
                    type="radio"
                    id={`promotion-${index}`}
                    name="promotion"
                    value={option.title}
                    checked={formData.promotion === option.title}
                    onChange={handleChange}
                    style={{
                      accentColor: '#b86fec', // Matches the theme
                      transform: 'scale(1.2)', // Slightly larger radio button
                    }}
                  />
                  <div
                  style={{
                    border: formData.promotion === option.title ? '2px solid #b86fec' : '1px solid #ddd', // Light purple border for selected option
                    borderRadius: '8px', // Rounded corners for a modern look
                    padding: '10px', // Adds spacing inside the box
                    marginBottom: '10px', // Adds spacing between options
                    backgroundColor: formData.promotion === option.title ? '#f9f9ff' : '#fff', // Slightly lighter background for selected option
                  }}>
                    <label htmlFor={`promotion-${index}`} style={{ color: '#333', fontSize: '1rem', fontWeight: 'bold' }}>
                      {option.title}
                    </label>
                    <p style={{ color: '#666', fontSize: '0.9rem', marginTop: '5px' }}>
                      {option.description} <br />
                      {option.price}
                    </p>
                    {formData.promotion === option.title && option.hint && (
                    <p style={{ color: '#b86fec', fontSize: '0.8rem', marginTop: '5px' }}>
                      {option.hint}
                    </p>
                    )}
                  </div>
                </div>
              ))}
              {/* Note about payment terms */}
              <p style={{ color: '#b86fec', fontSize: '0.9rem', marginTop: '15px', lineHeight: '1.5' }}>
                      <strong>*Note:</strong> Monthly payments are required for one year and auto-renew. After 12 months, cancellation is possible at any time with one month’s notice. <br />This price does not include the cost of the token contract, which must be paid separately ahead.
                    </p>
            </div>
          </div>
          <br />

          {/* Order Process Note */}
          <p style={{ fontSize: '0.9rem', color: '#888', marginTop: '10px', textAlign: 'center' }}>
            Want to get familiar with our order process?{' '}
            <a
              href="/TokenOrderProcess"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#b86fec', textDecoration: 'none', fontWeight: 'bold' }}
            >
              Click here
            </a>
            .
          </p>

          {/* Submit Button */}
          <StyledButton type="submit">Submit Request</StyledButton>
        </>)}



        </StyledForm>
        {successMessage && <SuccessMessage>{successMessage}</SuccessMessage>}
            <br />
        {/* Note Section */}
        <p style={{ fontSize: '0.9rem', color: '#888', marginTop: '20px', textAlign: 'center' }}>
          Your data will only be used to process your request. You can find our{' '}
          <a
            href="https://cryptobartl.com/Legals/"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#b86fec', textDecoration: 'none', fontWeight: 'bold' }}
          >
            privacy policy here
          </a>
          .
        </p>
        <br />
        <p style={{ fontSize: '0.9rem', color: '#888', marginTop: '10px', textAlign: 'center' }}>
          Please note: We are always here to support you further if needed. For more complex or extensive requests, additional costs may apply. Of course, we will always confirm and agree on any such costs with you in advance, ensuring full transparency and your approval before proceeding.
        </p>
      </StyledFormContainer>
      ): (
        <>
          {successMessage && (
                <div style={{ textAlign: 'center', marginTop: '20px', color: '#333' }}>
                  <p style={{ fontSize: '1rem', color: '#b86fec', fontWeight: 'bold' }}>
                    {successMessage}
                  </p>
                  <br />
                  <br />
                  <p style={{ fontSize: '0.9rem', color: '#666', marginTop: '10px' }}>
                    Please check in a few minutes your email for a confirmation message (please also check in your spam folder). If you do not receive an email in the next 10 minutes, please contact us at{' '}                    <a
                      href="mailto:cryptobartl_emb@outlook.com"
                      style={{ color: '#b86fec', textDecoration: 'none', fontWeight: 'bold' }}
                    >
                      cryptobartl_emb@outlook.com
                    </a>
                    .
                  </p>

                  <br/>
                  <br/>
                </div>
          )}

          <br />
          <br />

          {/* Order Process Note */}
          <p style={{ fontSize: '0.9rem', color: '#888', marginTop: '10px', textAlign: 'center' }}>
            Want to get familiar with our order process?{' '}
            <a
              href="/TokenOrderProcess"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#b86fec', textDecoration: 'none', fontWeight: 'bold' }}
            >
              Click here
            </a>
            .
          </p>
        <br />
        <br />

        <br />
        <br />
        
        <div style={{ textAlign: 'center', marginTop: '50px' }}>
          <button
            onClick={() => {
              setFormVisible(true); // Show the form
              setSuccessMessage(''); // Clear the success message
            }}
            style={{
              padding: '15px 30px',
              backgroundColor: '#333', // Dark background
              border: '2px solid #b86fec', // Purple border
              borderRadius: '15px',
              fontSize: '1.2rem',
              color: '#fff', // White text for contrast
              cursor: 'pointer',
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)', // Adds a subtle shadow
              transition: 'transform 0.2s ease, box-shadow 0.2s ease', // Smooth hover effect
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'scale(1.05)'; // Slightly enlarge on hover
              e.target.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.3)'; // Darker shadow on hover
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'scale(1)'; // Reset size
              e.target.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.2)'; // Reset shadow
            }}
          >
            <StyledHeader>Make a New Request</StyledHeader>
          </button>
        </div>
        </>

        
      )
    }
    </>
  );
}

export default TokenRequest;